import { useEffect, useState, type ReactNode } from 'react';
import { ActionIcon, Avatar, Box, Button, Center, Group, Loader, Select, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { SectionTitle } from './TitleSection';
import { IconBan, IconPencil, IconPlus, IconRotateClockwise, IconSearch, IconSend, IconTrash } from '@tabler/icons-react';
import { BadgeBlock } from '../../components/BadgeBlock';
import { InlineNotice } from '../../components/InlineNotice';
import { RoleEditorModal } from './components/RoleEditorModal';
import { InviteUserModal, type InvitedUser } from './components/InviteUserModal';
import type { RoleOption } from './components/roleEditorLogic';
import { useAuth } from '@workos-inc/authkit-react';
import { api, bearer } from '../../api/client';
import { fetchMe } from '../../api/me';
import { relativeTimeFrom } from '../../lib/relativeTime';
import type { components } from '../../api/generated/entitlements';

type ApiUser = components['schemas']['UserView'];

// Unknown backend statuses pass through raw rather than being silently coerced.
const STATUS_LABELS: Record<string, string> = {
  Active: 'Active',
  Invited: 'Invited',
  Deactivated: 'Suspended',
};

interface UserRow {
  id: string;
  email: string;
  name: string;
  roles: string[];
  status: string;
  last_activity: string;
}


function toRow(u: ApiUser): UserRow {
  return {
    id: u.id,
    email: u.email,
    name: u.name ?? u.email,
    roles: u.roles ?? (u.role ? [u.role] : []),
    status: STATUS_LABELS[u.status] ?? u.status,
    last_activity: relativeTimeFrom(u.lastActiveAt),
  };
}

const ACTION_ERRORS: Record<string, string> = {
  already_member: 'That email is already an active member of your organisation.',
  already_invited: 'That email already has a pending invitation.',
  invalid_request: 'That email address is not valid.',
  forbidden: "You don't have permission to manage users.",
  cannot_deactivate_self: "You can't suspend your own account.",
  cannot_deactivate_last_admin: "You can't suspend your organisation's last admin.",
  cannot_change_own_role: "You can't change your own organisation role.",
  cannot_demote_last_admin: "You can't remove your organisation's last admin.",
  invalid_roles: 'One or more selected roles are not available.',
  pending_invite: "A pending invitation can't be suspended or reactivated.",
  not_found: 'That user is no longer in your organisation.',
  sessions_not_revoked: 'The user was suspended, but signing them out failed — please retry.',
  workos_failed: "The identity service couldn't complete the action — try again shortly.",
  audit_unavailable: "The action couldn't be recorded to the audit log — try again shortly.",
};

function actionErrorText(code: string | undefined): string {
  return (code && ACTION_ERRORS[code]) || 'The action failed — please try again in a moment.';
}

// Invite actions act on an invitation, not a member, so not_found reads differently.
const INVITE_ERRORS: Record<string, string> = {
  not_found: 'That invitation is no longer pending.',
};

function inviteErrorText(code: string | undefined): string {
  return (code && INVITE_ERRORS[code]) || actionErrorText(code);
}

function sameRoleSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x.localeCompare(y));
  const sortedB = [...b].sort((x, y) => x.localeCompare(y));
  return sortedA.every((slug, i) => slug === sortedB[i]);
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts.at(-1)![0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

type IconTint = 'neutral' | 'danger' | 'success';

const ICON_TINTS: Record<IconTint, string> = {
  neutral: 'var(--text-muted)',
  danger: 'var(--status-danger)',
  success: 'var(--status-success)',
};

// Tinted icon-action button, matching the table's surface/border treatment.
function iconActionStyle(tint: IconTint) {
  const color = ICON_TINTS[tint];
  const tinted = tint !== 'neutral';
  return {
    // Via --ai-bg, not the background property: inline background would block the :hover repaint.
    '--ai-bg': 'var(--bg-surface)',
    '--ai-color': color,
    '--ai-bd': tinted ? `1px solid color-mix(in srgb, ${color}, transparent 60%)` : '1px solid var(--border-default)',
    '--ai-hover': tinted ? `color-mix(in srgb, ${color}, transparent 92%)` : 'var(--bg-subtle)',
  };
}

const NO_MANAGE_PERMISSION = "You don't have permission to manage users.";

// One icon action in a table row: tooltip + tinted ActionIcon.
function RowAction({ label, tooltip, icon, tint, disabled, onClick }: Readonly<{
  label: string;
  tooltip?: string;
  icon: ReactNode;
  tint: IconTint;
  disabled?: boolean;
  onClick: () => void;
}>) {
  return (
    // The span keeps the tooltip working while the button is disabled — Mantine drops
    // pointer events on a native disabled button, so the tooltip target must be its wrapper.
    <Tooltip label={tooltip ?? label}>
      <span style={{ display: 'inline-flex' }}>
        <ActionIcon
          variant="default"
          size="lg"
          aria-label={label}
          style={iconActionStyle(tint)}
          disabled={disabled}
          onClick={onClick}
        >
          {icon}
        </ActionIcon>
      </span>
    </Tooltip>
  );
}

// Per-row actions: invited rows manage the invite (resend/cancel); joined rows edit/suspend.
function RowActions({ user, canManage, busy, onEdit, onSuspend, onResend, onCancel }: Readonly<{
  user: UserRow;
  canManage: boolean;
  busy: boolean;
  onEdit: () => void;
  onSuspend: () => void;
  onResend: () => void;
  onCancel: () => void;
}>) {
  const noPerm = canManage ? undefined : NO_MANAGE_PERMISSION;
  if (user.status === 'Invited') {
    return (
      <Group gap={6} wrap="nowrap">
        <RowAction label="Resend invite" tooltip={noPerm} tint="success" disabled={!canManage || busy}
          onClick={onResend} icon={<IconSend size={17} />} />
        <RowAction label="Cancel invite" tooltip={noPerm} tint="danger" disabled={!canManage || busy}
          onClick={onCancel} icon={<IconTrash size={17} />} />
      </Group>
    );
  }
  const suspended = user.status === 'Suspended';
  return (
    <Group gap={6} wrap="nowrap">
      <RowAction label="Edit roles" tooltip={noPerm} tint="neutral" disabled={!canManage}
        onClick={onEdit} icon={<IconPencil size={17} />} />
      <RowAction
        label={suspended ? 'Reactivate' : 'Suspend'}
        tooltip={noPerm}
        tint={suspended ? 'neutral' : 'danger'}
        disabled={!canManage || busy}
        onClick={onSuspend}
        icon={suspended ? <IconRotateClockwise size={17} /> : <IconBan size={17} />}
      />
    </Group>
  );
}

function getStatusColor(status: string): string {
  if (status === 'Active') return 'var(--status-success)';
  if (status === 'Suspended') return 'var(--status-danger)';
  return 'var(--status-warning)';
}

function StatusBadge({ status }: Readonly<{ status: string }>) {
  const color = getStatusColor(status);
  return (
    <BadgeBlock
      color={color}
      py={3} h={'auto'}
      fz={12}
      fw={600}
      tt={'none'}
      bdrs={'xl'}
    >
      <Group gap={5} wrap="nowrap">
        <Box style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
        {status}
      </Group>
    </BadgeBlock>
  );
}

const pageData = {
  title: 'Users',
  subtitle: "Manage who can access your organisation's Cloudhouse applications.",
};

const KNOWN_STATUS_FILTERS = new Set(['Active', 'Invited', 'Suspended']);

export function ProfileUsers() {
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    const requested = searchParams.get('status');
    return requested && KNOWN_STATUS_FILTERS.has(requested) ? requested : 'all';
  });
  const [availableRoles, setAvailableRoles] = useState<RoleOption[]>([]);
  const [ownedProductKeys, setOwnedProductKeys] = useState<string[]>([]);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [editingSelected, setEditingSelected] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);

  const canManageUsers = (auth.permissions ?? []).includes('org-admin:user-write');

  async function loadUsers(signal?: AbortSignal): Promise<boolean> {
    const token = await auth.getAccessToken();
    if (!token) return false;
    const { data, error } = await api.GET('/users', { headers: bearer(token), signal });
    if (error || !data) return false;
    setUsers(data.users.map(toRow));
    return true;
  }

  // Assignable roles for the picker; a failure here shouldn't block the user list.
  async function loadRoles(signal?: AbortSignal): Promise<RoleOption[]> {
    const token = await auth.getAccessToken();
    if (!token) return [];
    const { data, error } = await api.GET('/users/roles', { headers: bearer(token), signal });
    if (error || !data) return [];
    return data.roles ?? [];
  }

  // Product keys the org holds an active entitlement for; gates which role sections
  // the editor offers. A failure here shouldn't block the user list either.
  async function loadOwnedProductKeys(signal?: AbortSignal): Promise<string[]> {
    const token = await auth.getAccessToken();
    if (!token) return [];
    const { products } = await fetchMe(token, signal);
    return Object.entries(products)
      .filter(([, entitlement]) => entitlement?.status === 'active')
      .map(([key]) => key);
  }

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        setLoadError(!(await loadUsers(ctrl.signal)));
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setLoadError(true);
      } finally {
        setLoading(false);
      }
    })();
    void (async () => {
      try {
        setAvailableRoles(await loadRoles(ctrl.signal));
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setAvailableRoles([]);
      }
    })();
    void (async () => {
      try {
        setOwnedProductKeys(await loadOwnedProductKeys(ctrl.signal));
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setOwnedProductKeys([]);
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchesSearch = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || u.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Suspend = deactivate the membership (revokes sessions server-side); Reactivate restores it.
  async function toggleSuspend(user: UserRow) {
    if (pendingId) return;
    setActionError(null);
    setActionNotice(null);
    setPendingId(user.id);
    try {
      const token = await auth.getAccessToken();
      if (!token) { setActionError(actionErrorText(undefined)); return; }

      const path = user.status === 'Suspended' ? '/users/{id}/reactivate' : '/users/{id}/deactivate';
      const { data, error } = await api.POST(path, {
        params: { path: { id: user.id } },
        headers: bearer(token),
      });
      if (error || !data) { setActionError(actionErrorText(error?.error)); return; }

      const status = data.status === 'Deactivated' ? 'Suspended' : 'Active';
      setUsers(prev => prev.map(u => (u.id === data.id ? { ...u, status } : u)));
    } catch {
      setActionError(actionErrorText(undefined));
    } finally {
      setPendingId(null);
    }
  }

  function closeRoleEditor() {
    setEditingUser(null);
    setSaveError(null);
  }

  async function saveRoles(email: string, roles: string[]) {
    const row = users.find(u => u.email === email);
    if (!row || pendingId) return;

    if (sameRoleSet(row.roles, roles)) {
      closeRoleEditor();
      return;
    }

    setSaveError(null);
    setPendingId(row.id);
    try {
      const token = await auth.getAccessToken();
      if (!token) { setSaveError(actionErrorText(undefined)); return; }

      const { data, error } = await api.PUT('/users/{id}/roles', {
        params: { path: { id: row.id } },
        body: { roles },
        headers: bearer(token),
      });
      if (error || !data) { setSaveError(actionErrorText(error?.error)); return; }

      setUsers(prev => prev.map(u => (u.id === row.id ? { ...u, roles: data.roles } : u)));
      if (auth.user?.email && row.email.toLowerCase() === auth.user.email.toLowerCase()) {
        // The new role has to reach this admin's own token now, not at the next scheduled refresh.
        try { await auth.getAccessToken({ forceRefresh: true }); } catch { /* the next refresh carries it */ }
      }
      closeRoleEditor();
    } catch {
      setSaveError(actionErrorText(undefined));
    } finally {
      setPendingId(null);
    }
  }

  function openRoleEditor(user: UserRow) {
    setEditingSelected(user.roles.length ? user.roles : ['member']);
    setSaveError(null);
    setEditingUser(user);
  }

  function removeUserRow(id: string) {
    setUsers(prev => prev.filter(u => u.id !== id));
  }

  // Shared shell for per-invite actions: guard, clear banners, call WorkOS, surface errors.
  // Actions resolve the pending invite by email, so they carry the row's email, not id.
  async function runInviteAction(
    user: UserRow,
    call: (token: string) => Promise<{ error?: { error?: string } }>,
    onSuccess: () => void,
  ) {
    if (pendingId) return;
    setActionError(null);
    setActionNotice(null);
    setPendingId(user.id);
    try {
      const token = await auth.getAccessToken();
      if (!token) { setActionError(actionErrorText(undefined)); return; }
      const { error } = await call(token);
      if (error) { setActionError(inviteErrorText(error.error)); return; }
      onSuccess();
    } catch {
      setActionError(actionErrorText(undefined));
    } finally {
      setPendingId(null);
    }
  }

  function resendInvite(user: UserRow) {
    return runInviteAction(
      user,
      token => api.POST('/users/invitations/resend', { body: { email: user.email }, headers: bearer(token) }),
      () => setActionNotice(`Invitation resent to ${user.email}.`),
    );
  }

  function cancelInvite(user: UserRow) {
    return runInviteAction(
      user,
      token => api.POST('/users/invitations/revoke', { body: { email: user.email }, headers: bearer(token) }),
      () => {
        // The invite is gone — drop the row; a refetch would no longer return it.
        removeUserRow(user.id);
        setActionNotice(`Invitation to ${user.email} cancelled.`);
      },
    );
  }

  async function handleInvite(invited: InvitedUser) {
    if (inviteBusy) return;
    setActionError(null);
    setActionNotice(null);
    setInviteBusy(true);
    try {
      const token = await auth.getAccessToken();
      if (!token) { setActionError(actionErrorText(undefined)); return; }

      const { error } = await api.POST('/users/invitations', {
        body: {
          email: invited.email,
          role: invited.roles[0] === 'admin' ? 'admin' : null,
          expiresInDays: null,
        },
        headers: bearer(token),
      });
      if (error) { setActionError(actionErrorText(error.error)); return; }

      // The invite itself succeeded — a refetch failure must not present as a load failure.
      let refreshed = false;
      try {
        refreshed = await loadUsers();
      } catch {
        refreshed = false;
      }
      if (!refreshed) setActionError('Invitation sent, but the list could not refresh — reload to see it.');
    } finally {
      setInviteBusy(false);
    }
  }

  return (
    <>
      <Stack flex={1} style={{ overflowY: 'auto' }} px={32} py={24} gap={0}>

        <SectionTitle {...pageData}>
          <Tooltip label="You don't have permission to manage users." disabled={canManageUsers}>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => setInviteOpen(true)}
              disabled={!canManageUsers}
            >
              Invite User
            </Button>
          </Tooltip>
        </SectionTitle>

        {loading && <Center py={20}><Loader size="sm" /></Center>}
        {loadError && (
          <InlineNotice tone="error"
            message="Couldn't load users from the account service — check the entitlements API is running." />
        )}
        {actionError && <InlineNotice tone="error" message={actionError} onClose={() => setActionError(null)} />}
        {actionNotice && <InlineNotice tone="success" message={actionNotice} onClose={() => setActionNotice(null)} />}

        <Group id="users-toolbar" data-testid="users-toolbar" gap={10} wrap="wrap" mt="lg" mb="md">
          <TextInput
            c={'var(--text-default)'}
            placeholder="Search users…"
            styles={{ wrapper: { '--input-placeholder-color': 'var(--text-subtle)' } }}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={15} color='var(--text-subtle)' />}
            w={280}
            data-input-cta
          />
          <Select
            placeholder="All statuses"
            defaultValue="all"
            data-input-cta
            allowDeselect={false}
            withAlignedLabels
            chevronColor='var(--text-subtle)'
            fw={600}
            onChange={(value) => setStatusFilter(value || 'all')}
            value={statusFilter}
            styles={{
              dropdown: {
                backgroundColor: 'var(--bg-surface)',
                border: '1px solid var(--border-default)'
              },
              option: {
                fontWeight: 600,
                '--mantine-color-dark-7': 'var(--bg-subtle)',
              }
            }}
            data={[
              { value: 'all', label: 'All statuses' },
              { value: 'Active', label: 'Active' },
              { value: 'Suspended', label: 'Suspended' },
              { value: 'Invited', label: 'Invited' },
            ]}
            w={180}
          />
        </Group>

        <Box
          id="users-table"
          style={{
            border: '1px solid var(--border-default)',
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            background: 'var(--bg-surface)',
            flex: 'none',

          }}
        >
          <Table highlightOnHover={filtered.length > 0} style={{
            '--table-vertical-spacing': '14px 18px',
            '--table-border-color': 'var(--border-default)',
            '--table-hover-color': 'var(--bg-subtle)',

          }}>
            <Table.Thead>
              <Table.Tr style={{
                '--table-vertical-spacing': '11px 18px',
                color: 'var(--text-subtle)',
                fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase',
                background: 'var(--bg-subtle)',
              }}>
                <Table.Th>User</Table.Th>
                <Table.Th>Roles</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Last activity</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {!loading && filtered.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Stack align="center" gap={6} py={40}>
                      <Text fz="sm" fw={600} c="var(--text-default)">No users found</Text>
                      <Text fz="xs" c="var(--text-subtle)">Try adjusting your search or filter.</Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )}
              {filtered.map(user => (
                <Table.Tr key={user.id}>
                  <Table.Td>
                    <Group gap={11} wrap="nowrap">
                      <Avatar size={32} radius="xl" color="white"
                        lh={'normal'}
                        style={{
                          '--avatar-bg': 'linear-gradient(135deg, rgb(81, 100, 112), rgb(13, 40, 57))',
                          '--avatar-bd': 'none',
                        }}>
                        {getInitials(user.name)}
                      </Avatar>
                      <Stack gap={0}>
                        <Text fz="sm" fw={600} lh="normal">{user.name}</Text>
                        <Text fz="xs" fw={500} c="var(--text-subtle)" lh="normal">{user.email}</Text>
                      </Stack>
                    </Group>
                  </Table.Td>

                  <Table.Td>
                    {user.roles.length === 0 ? (
                      <Text c="var(--text-subtle)" fz="sm">—</Text>
                    ) : (
                      <Group gap={4} wrap="wrap">
                        {user.roles.map(role => (
                          <BadgeBlock
                            color="var(--accent-text)"
                            py={2} h={'auto'}
                            key={role}
                            fz={12}
                            fw={600}
                            tt={'none'}
                            bdrs={6}
                            bg={'var(--brand-soft)'}
                          >
                            {role}
                          </BadgeBlock>
                        ))}
                      </Group>
                    )}
                  </Table.Td>

                  <Table.Td>
                    <StatusBadge status={user.status} />
                  </Table.Td>

                  <Table.Td>
                    <Text fz="sm" c="var(--text-default)">{user.last_activity}</Text>
                  </Table.Td>

                  <Table.Td>
                    <RowActions
                      user={user}
                      canManage={canManageUsers}
                      busy={pendingId === user.id}
                      onEdit={() => openRoleEditor(user)}
                      onSuspend={() => { void toggleSuspend(user); }}
                      onResend={() => { void resendInvite(user); }}
                      onCancel={() => { void cancelInvite(user); }}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Stack>


      <InviteUserModal
        opened={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvite={handleInvite}
      />
      <RoleEditorModal
        user={editingUser}
        available={availableRoles}
        selected={editingSelected}
        onSelectedChange={setEditingSelected}
        onClose={closeRoleEditor}
        onSave={saveRoles}
        error={saveError}
        ownedProductKeys={ownedProductKeys}
      />
    </>

  );
}
